import { FlightData, LatLng } from '../types'
import L from 'leaflet'
import PlaneMarker from '../img/plane.png'
import { getCurrentFlight } from '../tracker/utils'
import bearing from '@turf/bearing'
import { Feature, lineString, LineString, point } from '@turf/helpers'
import { differenceInMinutes, sleep } from '../utils'
import length from '@turf/length'
import along from '@turf/along'
import { t } from 'i18next'
import { Path } from './path'
import nearestPointOnLine from '@turf/nearest-point-on-line'

export class Plane {
  map: L.Map
  path: Path
  speedUnit: 'M' | 'K'
  showTooltip: boolean
  icon: L.Icon
  marker: L.Marker
  replay: boolean = false

  constructor (map: L.Map, planeSize: number = 60, speedUnit: 'M' | 'K' = 'M', showTooltip: boolean = true) {
    this.map = map
    this.showTooltip = showTooltip
    this.icon = L.icon({
      iconUrl: PlaneMarker,
      iconSize: [planeSize, planeSize],
      iconAnchor: [planeSize / 2, planeSize / 2]
    })
    this.path = new Path(map)
  }

  apply (flights: FlightData[]) {
    this.path.apply(flights)
    const flight = getCurrentFlight(flights)
    let position, heading
    if (!flight.position || !flight.position.lat || !flight.position.lng) {
      if (flight.status.includes('Scheduled') || flight.status.includes('Delayed') || flight.status.includes('Proposed') || flight.status.includes('OutGate')) {
        position = flight.departureAirport.coords
      } else if (flight.status.includes('InAir')) {
        position = this.estimatePlanePosition(flight)
        if (position) {
          heading = bearing(point([position.lng, position.lat]), point([flight.arrivalAirport.coords.lng, flight.arrivalAirport.coords.lat]))
        }
      } else if (flight.status.includes('InGate') || flight.status.includes('Landed')) {
        position = flight.arrivalAirport.coords
      }
    } else {
      position = this.antiMeridianLngFix({
        lat: flight.position.lat,
        lng: flight.position.lng
      }, flight.departureAirport.coords)
      heading = flight.position.heading
    }
    if (position) {
      if (this.marker) {
        this.marker
          .setLatLng(position)
          .setRotationAngle(heading)
      } else {
        this.marker = L.marker(position, {
          zIndexOffset: 500,
          icon: this.icon,
          rotationAngle: heading || 0
        }).addTo(this.map)
      }
    } else if (this.marker) {
      this.map.removeLayer(this.marker)
      this.marker = undefined
    }
    if (this.showTooltip && this.marker && flight.status.includes('InAir')) {
      this.planeTooltip(flight)
    }
  }

  async movePlaneAlongPath (flights: FlightData[]) {
    const oldCoords = this.marker.getLatLng()
    const coords = this.path.path.getLatLngs() as LatLng[]
    const line = this.generateTurfLine(coords)
    const closestPoint = nearestPointOnLine(line, point([oldCoords.lng, oldCoords.lat]))
    while (this.replay) {
      for (let i = 0; i < coords.length; i++) {
        if (!this.replay) break
        const coord = coords[i]
        const nextCoord = i !== coords.length ? coords[i + 1] : null
        this.marker.setLatLng(coord)
        if (nextCoord) {
          const angle = bearing(point([coord.lng, coord.lat]), point([nextCoord.lng, nextCoord.lat]))
          this.marker.setRotationAngle(angle)
        }
        await sleep(50)
        if (i === closestPoint.properties.index) {
          await sleep(500)
          break
        }
      }
    }
    this.apply(flights)
  }

  /**
   * Estimate plane position by calculating the percentage of time already in air / total flight time.
   * Once we have a percentage, find the point of the path for that percentage.
   * @returns {null|{lng: number, lat: number}}
   */
  estimatePlanePosition (flight: FlightData): LatLng | null {
    const dep = flight.departureAirport
    const arr = flight.arrivalAirport

    if (dep?.time && arr && arr.time && this.path.path) {
      const flightDuration = differenceInMinutes(arr.utc.time.getTime(), dep.utc.time.getTime())
      const now = new Date()
      const timeSpent = differenceInMinutes(now.getTime(), dep.utc.time.getTime())
      const progress = timeSpent / flightDuration
      const turfCoords = this.generateTurfLine(this.path.path.getLatLngs() as LatLng[])
      const pathLength = length(turfCoords)
      const distance = Math.abs(pathLength * progress)
      const estimatedPos = along(turfCoords, distance)
      const latLng = { lng: estimatedPos.geometry.coordinates[0], lat: estimatedPos.geometry.coordinates[1] }
      return this.antiMeridianLngFix(latLng, flight.departureAirport.coords)
    } else {
      return null
    }
  }

  generateTurfLine (coords: LatLng[]): Feature<LineString> {
    const coordsArray = coords.map((latLng) => [latLng.lng, latLng.lat])
    return lineString(coordsArray)
  }

  planeTooltip (flight: FlightData) {
    const flightInfoDiv = document.createElement('div')
    flightInfoDiv.className = 'FVLib-flight-info'

    if (flight.flightInfo.aircraftType) {
      flightInfoDiv.appendChild(this.createInfoItem(t('TRACKER.MAP_PART.TOOLTIP_AIRCRAFT', {
        aircraft: flight.flightInfo.aircraftType
      })))
    }
    if (flight.flightInfo.registration) {
      flightInfoDiv.appendChild(this.createInfoItem(t('TRACKER.MAP_PART.TOOLTIP_REGISTRATION', {
        registration: flight.flightInfo.registration
      })))
    }
    if (flight.flightInfo.speed) {
      const formatted = Math.round(this.speedUnit === 'M' ? flight.flightInfo.speed * 1.15077945 : flight.flightInfo.speed * 1.852)
      const unit = this.speedUnit === 'M' ? 'mph' : 'kmph'
      flightInfoDiv.appendChild(this.createInfoItem(t('TRACKER.MAP_PART.TOOLTIP_GROUND_SPEED', {
        value: formatted,
        unit
      })))
    }
    if (flight.flightInfo.altitude) {
      const normalValue = Intl.NumberFormat().format(flight.flightInfo.altitude)
      flightInfoDiv.appendChild(this.createInfoItem(t('TRACKER.MAP_PART.TOOLTIP_ALTITUDE', {
        altitude: normalValue.toString()
      })))
    }
    this.marker.unbindTooltip()
    this.marker.bindTooltip(flightInfoDiv,
      {
        direction: 'top',
        permanent: true,
        sticky: true,
        offset: [0, 0],
        opacity: 0.75,
        className: 'leaflet-tooltip-own'
      }
    )
  }

  createInfoItem (label: string): Element {
    const infoItem = document.createElement('div')
    const labelSpan = document.createElement('span')
    labelSpan.innerText = label

    infoItem.appendChild(labelSpan)

    return infoItem
  }

  antiMeridianLngFix (latLng: LatLng, prevLatLng: LatLng): LatLng {
    // needed when we cross the 180th meridian
    latLng.lng += latLng.lng - prevLatLng.lng > 180 ? -360 : prevLatLng.lng - latLng.lng > 180 ? 360 : 0
    return latLng
  }
}
